import { Subscription } from '@services/subscription/types/subscription';
import { LabelPlanNames, PlanNames } from '@services/product/types/plan-names';
import { Order } from '../../types/orders/order';
import { NewSubscription } from '@services/subscription/types/new-subscription';
import { SubscriptionType } from '@services/subscription/types/subscription-type';
import { PaymentFrequency } from '@services/product/types/payment-frequency';
import AppState from '../state';

export const isPlanHigherThan = (
  subscription: Subscription | undefined,
  plan: string
) => {
  if (!subscription) {
    return false;
  }

  switch (subscription?.plan?.toUpperCase()) {
    case 'BASIC':
    case 'BEGINNER':
      return false;
    case 'GROWTH':
      return ['BASIC', 'BEGINNER'].includes(plan?.toUpperCase());
    case 'PRO':
    case 'TRAC PRO':
    case 'DISTRIBUTION PRO':
      return ['BASIC', 'BEGINNER', 'GROWTH'].includes(plan?.toUpperCase());
    default:
      return false;
  }
};

export const isBusinessLabel = (subscription?: Subscription): boolean => {
  return subscription?.plan?.toUpperCase() === 'LABEL';
};

export const getSubscriptionChange = (
  current: undefined | Subscription,
  newPlan: PlanNames | LabelPlanNames | 'merch pro',
  newFrequency: PaymentFrequency,
  isCurrentSubscriptionExpiring?: boolean
): undefined | 'UPGRADE' | 'DOWNGRADE' | 'IGNORE' | 'FREQUENCY' => {
  if (!current) {
    if (newPlan === 'merch pro') {
      return 'UPGRADE';
    }
    return;
  }

  if (!current.isActive) {
    return undefined;
  }

  newPlan = newPlan?.toLowerCase() as PlanNames | LabelPlanNames;
  const currentPlan = current.plan?.toLowerCase() as
    | 'basic'
    | PlanNames
    | LabelPlanNames;

  if (currentPlan === newPlan) {
    if (isCurrentSubscriptionExpiring) {
      return 'UPGRADE';
    }
    if (current.frequency === newFrequency) {
      return 'IGNORE';
    }

    return 'FREQUENCY';
  }

  if (currentPlan === 'basic' && newPlan === 'beginner') {
    return 'IGNORE';
  }

  switch (currentPlan) {
    case 'basic': {
      return 'UPGRADE';
    }
    case 'beginner': {
      return 'UPGRADE';
    }
    case 'team': {
      return 'UPGRADE';
    }
    case 'growth': {
      if (['basic', 'beginner'].includes(newPlan)) {
        return 'DOWNGRADE';
      }
      return 'UPGRADE';
    }
    case 'label': {
      return 'DOWNGRADE';
    }
    case 'pro':
    case 'distribution pro': {
      if (['basic', 'beginner', 'growth'].includes(newPlan)) {
        return 'DOWNGRADE';
      }
      return 'UPGRADE';
    }

    case 'premium': {
      return 'DOWNGRADE';
    }
    default:
      return;
  }
};

export const isNewSubscription = (
  currentSubscription: Subscription | undefined
): boolean => {
  return (
    currentSubscription === undefined ||
    ['BASIC', 'BEGINNER', 'TEAM'].includes(
      currentSubscription.plan.toUpperCase()
    ) ||
    !currentSubscription.isActive ||
    !(currentSubscription.externalId || currentSubscription.paypalId)
  );
};

export const buildNewSubscription = (
  clientId: string,
  type: SubscriptionType,
  order: Order
): NewSubscription => ({
  clientId,
  type,
  product: {
    id: order.product.id as string,
    name: order.product.name as string,
    category: order.product.category as string,
  },
  price: {
    id: order.price.id as string,
    category: order.price.category,
    frequency: order.price.frequency as PaymentFrequency,
  },
  plan: order.product.name as string,
  frequency: order.price.frequency as PaymentFrequency,
});

export const hasProLabel = (state: AppState): boolean =>
  Object.values(state.subscription.label).some(
    (r) => r?.plan?.toUpperCase() === 'LABEL' && r.isActive
  );

export const hasMerchSubscription = (state: AppState): boolean => {
  // only enable this to selected users
  const clientId = state.artist.active?.id;

  const artistSubs = state.subscriptionManager.summary?.artists.find(
    (a) => a.id === clientId
  );

  if (!artistSubs) {
    return false;
  }

  return artistSubs.subscriptions.some((s) => s.type === 'merch' && s.isActive);
};

export const isMerchCancelling = (state: AppState): boolean => {
  // only enable this to selected users
  const clientId = state.artist.active?.id;

  const artistSubs = state.subscriptionManager.summary?.artists.find(
    (a) => a.id === clientId
  );

  if (!artistSubs) {
    return false;
  }

  return artistSubs.subscriptions.some(
    (s) => s.type === 'merch' && s.isExpiring
  );
};
